import {
  AsYouType,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/min';
import { MEMBER_INVITE_BLOCK_MESSAGE } from './gift-invite-welcome';

export type SentBucket = 'pending' | 'partly_redeemed' | 'redeemed' | 'cancelled';

export type ExpireChoice = '1' | '3' | '7' | '14' | '21' | 'custom' | 'never';

const EXPIRE_DAY_PRESETS = [1, 3, 7, 14, 21] as const;

const PENDING = new Set(['pending', 'clicked', 'signed_up']);
const CANCELLED = new Set(['revoked', 'declined', 'expired']);

function toCountryCode(countryCode: string): CountryCode {
  return countryCode.toUpperCase() as CountryCode;
}

export const MISSING_INVITE_URL_MESSAGE =
  'This link cannot be shown again. Cancel this gift card and create a new invite. A link you already copied still works.';

export function sentBucket(input: {
  status?: string | null;
  deliveredToInventory?: boolean | null;
}): SentBucket {
  const status = String(input.status || '');
  if (input.deliveredToInventory && !CANCELLED.has(status)) return 'redeemed';
  if (PENDING.has(status)) return 'pending';
  if (status === 'partly_redeemed') return 'partly_redeemed';
  if (status === 'accepted') return 'redeemed';
  return 'cancelled';
}

export function expiryChoices(daysLeft: number | null, allowNever: boolean): ExpireChoice[] {
  const choices: ExpireChoice[] = [];
  EXPIRE_DAY_PRESETS.forEach((preset) => {
    if (daysLeft == null || preset <= daysLeft) choices.push(String(preset) as ExpireChoice);
  });
  choices.push('custom');
  if (allowNever && daysLeft == null) choices.push('never');
  return choices;
}

export function selectedExpireChoice(options: readonly ExpireChoice[], current: string): ExpireChoice {
  if (options.includes(current as ExpireChoice)) return current as ExpireChoice;
  return options[0] || '3';
}

export function customDayMax(daysLeft: number | null): number {
  if (daysLeft == null) return 90;
  return Math.min(90, Math.max(0, daysLeft));
}

export function boundedCustomDays(customDays: number, daysLeft: number | null): number {
  const max = customDayMax(daysLeft);
  if (max < 1 || !Number.isInteger(customDays) || customDays <= max) return customDays;
  return max;
}

export type SendHoldingStock = {
  countLeft: number;
  daysLeft: number | null;
  createdAt?: string | null;
};

export type SendStock = {
  selfPurchased: number;
  holdings: SendHoldingStock[];
  complementary: number;
  complementaryDaysLeft: number | null;
};

export const PASS_TYPE_FIELD_LABEL = 'Lifetime Access Pass Type';

export const PASS_TYPE_OPTION: Record<'SINGLE' | 'DUAL', string> = {
  SINGLE: 'Single LA Pass (either iPhone or Android)',
  DUAL: 'Dual LA Pass (both iPhone and Android)',
};

export function orderSendHoldings<T extends SendHoldingStock>(rows: readonly T[]): T[] {
  return [...rows].sort((left, right) => {
    const leftDays = left.daysLeft == null ? Number.POSITIVE_INFINITY : left.daysLeft;
    const rightDays = right.daysLeft == null ? Number.POSITIVE_INFINITY : right.daysLeft;
    if (leftDays !== rightDays) return leftDays - rightDays;
    const leftCreated = left.createdAt ? Date.parse(left.createdAt) : 0;
    const rightCreated = right.createdAt ? Date.parse(right.createdAt) : 0;
    const leftTime = Number.isNaN(leftCreated) ? 0 : leftCreated;
    const rightTime = Number.isNaN(rightCreated) ? 0 : rightCreated;
    return leftTime - rightTime;
  });
}

export function sendDrawPreview(stock: SendStock, quantity: number): {
  available: number;
  daysLeft: number | null;
  allowNever: boolean;
} {
  const holdings = orderSendHoldings(stock.holdings).filter((row) => row.countLeft > 0);
  const selfPurchased = Math.max(0, stock.selfPurchased);
  const complementary = Math.max(0, stock.complementary);
  const gifted = holdings.reduce((sum, row) => sum + row.countLeft, 0);
  const available = selfPurchased + gifted + complementary;
  const draw = Math.min(Math.max(0, quantity), available);
  if (draw <= selfPurchased) {
    return { available, daysLeft: null, allowNever: true };
  }
  let left = draw - selfPurchased;
  let cap: number | null = null;
  const tighten = (days: number | null, count: number) => {
    const taken = Math.min(left, count);
    if (taken > 0 && days != null) cap = cap == null ? days : Math.min(cap, days);
    left -= taken;
  };
  holdings.forEach((row) => {
    if (left <= 0) return;
    tighten(row.daysLeft, row.countLeft);
  });
  if (left > 0) tighten(stock.complementaryDaysLeft, complementary);
  return { available, daysLeft: cap, allowNever: false };
}

export function expirySelectionFits(
  preset: string,
  customDays: number,
  daysLeft: number | null,
  allowNever: boolean,
): boolean {
  if (!expiryChoices(daysLeft, allowNever).includes(preset as ExpireChoice)) return false;
  if (preset === 'never') return allowNever && daysLeft == null;
  const days = preset === 'custom' ? customDays : Number(preset);
  if (!Number.isInteger(days) || days < 1 || days > 90) return false;
  if (daysLeft != null && days > daysLeft) return false;
  return true;
}

export function formatNationalPhone(value: string, countryCode: string): string {
  return new AsYouType(toCountryCode(countryCode)).input(value);
}

export function toE164Phone(value: string, countryCode: string): string | null {
  const parsed = parsePhoneNumberFromString(value, toCountryCode(countryCode));
  if (!parsed || !parsed.isPossible()) return null;
  return parsed.number;
}

export function contactReachable(input: {
  email: string;
  phone: string;
  countryCode: string;
}): boolean {
  const emailOk = /\S+@\S+\.\S+/.test(input.email.trim());
  const phoneTrim = input.phone.trim();
  if (phoneTrim && !toE164Phone(phoneTrim, input.countryCode)) return false;
  return emailOk || Boolean(phoneTrim);
}

export function inviteCreateBlockReason(input: {
  hasCard: boolean;
  expiryOk: boolean;
  scope: 'SINGLE' | 'DUAL';
  sendMode: number;
  savedContactIsMember: boolean;
  hasSelectedContact: boolean;
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  expireValue: string;
  customDays: number;
  daysLeft: number | null;
}): string {
  if (!input.hasCard) {
    const pass = input.scope === 'DUAL' ? 'Dual' : 'Single';
    return `You do not have a ${pass} LA Pass gift card to send.`;
  }
  if (!input.expiryOk) {
    if (input.daysLeft != null && input.daysLeft >= 1) {
      return `Choose an expiry of ${input.daysLeft} days or fewer.`;
    }
    return 'Choose an expiry that fits this gift card.';
  }
  if (input.savedContactIsMember) return MEMBER_INVITE_BLOCK_MESSAGE;
  if (input.sendMode === 0 && !input.hasSelectedContact) {
    return 'Search for a contact and select one.';
  }
  if (input.sendMode !== 1) return '';
  const nameOk = Boolean(input.name.trim());
  const phoneTrim = input.phone.trim();
  if (phoneTrim && !toE164Phone(phoneTrim, input.countryCode)) {
    return 'Enter a valid phone number, or leave phone empty.';
  }
  const emailOk = /\S+@\S+\.\S+/.test(input.email.trim());
  if (!nameOk && !emailOk && !phoneTrim) {
    return 'Enter a contact name and an email or phone number.';
  }
  if (!nameOk) return 'Enter a contact name.';
  if (!emailOk && !phoneTrim) return 'Enter an email or phone number.';
  return '';
}

export function passStatusLabel(value?: string | null): string {
  if (value === 'SINGLE') return 'Single';
  if (value === 'DUAL') return 'Dual';
  return 'None';
}

export function memberDisplayName(member?: {
  username?: string | null;
  channelName?: string | null;
} | null): string {
  if (!member) return '';
  return member.username || member.channelName || '';
}

export function formatJoinDate(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function formatJoinedLabel(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
  return `Joined ${formatted}`;
}

export function memberRowName(member: {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  channelName?: string | null;
}): string {
  const named = `${member.firstName || ''} ${member.lastName || ''}`.trim();
  return named || member.displayName || member.channelName || '';
}

export function memberInitials(member: {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  channelName?: string | null;
}): string {
  const first = (member.firstName || '').trim();
  const last = (member.lastName || '').trim();
  const letters = `${first.charAt(0)}${last.charAt(0)}`.trim();
  if (letters) return letters.toUpperCase();
  const fallback = (member.channelName || member.displayName || '').trim();
  return fallback.charAt(0).toUpperCase();
}

export type GiftPass = 'NONE' | 'SINGLE' | 'DUAL';

export type GiftRecipientRoute =
  | { kind: 'email-not-applicable' }
  | { kind: 'not-registered' }
  | { kind: 'checking' }
  | { kind: 'failed' }
  | { kind: 'invalid-email' }
  | { kind: 'self' }
  | { kind: 'name-only' }
  | { kind: 'transfer-one'; memberId: string; channelName: string }
  | { kind: 'has-pass'; pass: 'SINGLE' | 'DUAL' }
  | { kind: 'missing-member' };

export function normalizeGiftEmail(email: string): string {
  return String(email || '').trim().toLowerCase();
}

export function isGiftEmail(email: string): boolean {
  return /\S+@\S+\.\S+/.test(String(email || '').trim());
}

export function giftRecipientRoute(input: {
  email: string;
  lookupStatus: 'idle' | 'checking' | 'failed' | 'ready';
  lookupEmail: string;
  registered?: boolean;
  memberId?: string;
  channelName?: string;
  pass?: GiftPass | null;
  senderId?: string;
  senderEmail?: string;
  nameOnly?: boolean;
  self?: boolean;
}): GiftRecipientRoute {
  if (input.nameOnly) return { kind: 'name-only' };
  if (input.self) return { kind: 'self' };
  const email = String(input.email || '').trim();
  if (!email) return { kind: 'email-not-applicable' };
  if (!isGiftEmail(email)) return { kind: 'invalid-email' };
  const normalized = normalizeGiftEmail(email);
  if (input.senderEmail && normalized === normalizeGiftEmail(input.senderEmail)) {
    return { kind: 'self' };
  }
  const lookupEmail = normalizeGiftEmail(input.lookupEmail);
  if (input.lookupStatus === 'failed' && lookupEmail === normalized) return { kind: 'failed' };
  if (input.lookupStatus !== 'ready' || lookupEmail !== normalized) return { kind: 'checking' };
  if (!input.registered) return { kind: 'not-registered' };
  if (!input.memberId) return { kind: 'missing-member' };
  if (input.senderId && String(input.memberId) === String(input.senderId)) return { kind: 'self' };
  const pass = input.pass || 'NONE';
  if (pass === 'SINGLE' || pass === 'DUAL') return { kind: 'has-pass', pass };
  return {
    kind: 'transfer-one',
    memberId: String(input.memberId),
    channelName: input.channelName || '',
  };
}

export function savedContactGiftRoute(contact: {
  id?: string;
  email?: string;
  disabledReason?: string | null;
  member: {
    id?: string;
    channelName?: string;
    username?: string;
    pass?: GiftPass | null;
  } | null;
} | null, sender?: { id?: string; email?: string }): GiftRecipientRoute {
  if (!contact) return { kind: 'email-not-applicable' };
  return giftRecipientRoute({
    email: contact.email || '',
    lookupStatus: 'ready',
    lookupEmail: contact.email || '',
    registered: Boolean(contact.member),
    memberId: contact.member?.id,
    channelName: contact.member?.channelName || contact.member?.username || '',
    pass: contact.member?.pass,
    senderId: sender?.id,
    senderEmail: sender?.email,
    nameOnly: contact.disabledReason === 'name_only',
    self: contact.disabledReason === 'self',
  });
}

export function giftNoticeWarns(route: GiftRecipientRoute): boolean {
  return route.kind === 'has-pass'
    || route.kind === 'failed'
    || route.kind === 'missing-member'
    || route.kind === 'invalid-email'
    || route.kind === 'self'
    || route.kind === 'name-only';
}

export function giftRecipientNotice(route: GiftRecipientRoute): string {
  if (route.kind === 'checking') return 'Checking this email…';
  if (route.kind === 'failed' || route.kind === 'missing-member') {
    return 'This email could not be checked. Try again.';
  }
  if (route.kind === 'invalid-email') return 'Enter a valid email address.';
  if (route.kind === 'self') return 'You cannot gift Lifetime Access to yourself.';
  if (route.kind === 'name-only') return 'Add an email or phone before sending.';
  if (route.kind === 'has-pass') {
    const label = route.pass === 'DUAL' ? 'Dual' : 'Single';
    return `The email address is already registered and has a ${label} LA Pass.`;
  }
  if (route.kind === 'transfer-one') return 'This email is registered and has no LA Pass.';
  return '';
}

export function giftRecipientAllowsInvite(route: GiftRecipientRoute): boolean {
  return route.kind === 'not-registered' || route.kind === 'email-not-applicable';
}

export function confirmedTransferMember(input: {
  route: GiftRecipientRoute;
  readyEmail?: string;
  currentEmail?: string;
  readyMemberId?: string;
  savedContactId?: string;
  currentContactId?: string;
}): { id: string; channelName: string } | null {
  if (input.route.kind !== 'transfer-one') return null;
  if (input.savedContactId || input.currentContactId) {
    if (!input.savedContactId || input.savedContactId !== input.currentContactId) return null;
    if (input.readyMemberId !== input.route.memberId) return null;
    return { id: input.route.memberId, channelName: input.route.channelName };
  }
  const ready = normalizeGiftEmail(input.readyEmail || '');
  const current = normalizeGiftEmail(input.currentEmail || '');
  if (!ready || ready !== current || input.readyMemberId !== input.route.memberId) return null;
  return { id: input.route.memberId, channelName: input.route.channelName };
}

export function countryFromPhone(value: string): string {
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.country?.toLowerCase() || 'us';
}

export function nationalPhoneDisplay(value: string, countryCode: string): string {
  const parsed = parsePhoneNumberFromString(value, toCountryCode(countryCode));
  if (!parsed) return formatNationalPhone(value, countryCode);
  return parsed.formatNational();
}

export function wholeDaysLeft(iso?: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - now;
  if (ms <= 0) return 0;
  return Math.floor(ms / 86400000);
}

export function choiceFromDays(days: number | null): { preset: ExpireChoice; custom: number } {
  if (days == null) return { preset: 'never', custom: 3 };
  if ((EXPIRE_DAY_PRESETS as readonly number[]).includes(days)) {
    return { preset: String(days) as ExpireChoice, custom: 3 };
  }
  return { preset: 'custom', custom: Math.min(90, Math.max(1, days)) };
}

export type GiftReportRow = {
  quantity?: number | null;
  redeemedCount?: number | null;
  extrasRemaining?: number | null;
};

export type GiftReportLists = {
  pending: GiftReportRow[];
  partlyRedeemed: GiftReportRow[];
  redeemed: GiftReportRow[];
  cancelled: GiftReportRow[];
};

export type AcceptedList = 'redeemed' | 'partly_redeemed';

export type GiftReport = {
  sent: number;
  pending: number;
  accepted: number;
  partlyRedeemed: number;
  cancelled: number;
  received: number;
  acceptedList: AcceptedList;
};

function cardCount(quantity: number | null | undefined): number {
  const value = Number(quantity);
  if (!Number.isInteger(value) || value < 1) return 1;
  return value;
}

function acceptedPasses(rows: GiftReportRow[], useQuantityWhenMissing: boolean): number {
  return rows.reduce((sum, row) => {
    if (row.redeemedCount == null) {
      return sum + (useQuantityWhenMissing ? cardCount(row.quantity) : 0);
    }
    const value = Number(row.redeemedCount);
    if (!Number.isFinite(value) || value < 0) return sum;
    return sum + value;
  }, 0);
}

export function receivedBadgeCount(lists: { new: number; accepted: number; declined: number }): number {
  return lists.new + lists.accepted + lists.declined;
}

export function giftReportCounts(lists: GiftReportLists, received: number): GiftReport {
  const pending = lists.pending.reduce((sum, row) => sum + cardCount(row.quantity), 0);
  const acceptedOnRedeemed = acceptedPasses(lists.redeemed, true);
  const acceptedOnPartly = acceptedPasses(lists.partlyRedeemed, false);
  const partlyRedeemed = lists.partlyRedeemed.reduce((sum, row) => {
    const extras = Number(row.extrasRemaining);
    if (!Number.isFinite(extras) || extras < 0) return sum;
    return sum + extras;
  }, 0);
  const cancelled = lists.cancelled.reduce((sum, row) => sum + cardCount(row.quantity), 0);
  const accepted = acceptedOnRedeemed + acceptedOnPartly;
  const acceptedList: AcceptedList = acceptedOnRedeemed === 0 && acceptedOnPartly > 0
    ? 'partly_redeemed'
    : 'redeemed';
  return {
    sent: pending + accepted + partlyRedeemed + cancelled,
    pending,
    accepted,
    partlyRedeemed,
    cancelled,
    received,
    acceptedList,
  };
}

export function sentCountLine(gift: {
  quantity?: number | null;
  redeemedCount?: number | null;
  extrasRemaining?: number | null;
}): string {
  const quantityValue = Number(gift.quantity);
  const quantity = Number.isInteger(quantityValue) && quantityValue > 0 ? quantityValue : 1;
  const redeemedValue = Number(gift.redeemedCount);
  const extrasValue = Number(gift.extrasRemaining);
  const redeemed = Number.isFinite(redeemedValue) && redeemedValue > 0 ? redeemedValue : 0;
  const extras = Number.isFinite(extrasValue) && extrasValue > 0 ? extrasValue : 0;
  return `${quantity} reserved · ${redeemed} redeemed · ${extras} extras left`;
}

export function receivedCardCountLine(quantity: number | null | undefined, scope: string | null | undefined): string {
  const count = Number.isInteger(Number(quantity)) && Number(quantity) > 0 ? Number(quantity) : 1;
  const kind = scope === 'DUAL' ? 'Dual' : 'Single';
  const noun = count === 1 ? 'Gift Card' : 'Gift Cards';
  return `${count} ${kind} LA Pass ${noun}`;
}

export function ownPassOutcomeLine(outcome: string | null | undefined): string | null {
  if (outcome === 'grant_single') return '1 was used to activate your own Single LA Pass.';
  if (outcome === 'grant_dual' || outcome === 'accept_dual_refund_single') {
    return '1 was used to activate your own Dual LA Pass.';
  }
  if (outcome === 'upgrade_to_dual') return '1 was used to upgrade you to Dual LA Pass.';
  if (outcome === 'grant_dual_mint_single') {
    return '1 was used to activate your own Dual LA Pass. 1 Single card was added for you to give away.';
  }
  if (outcome === 'park_gift') return 'No pass was activated. Every card was added for you to give away.';
  return null;
}

export function sentListStatusLabel(list: 'partly_redeemed' | 'redeemed'): string {
  if (list === 'partly_redeemed') return 'Status: Partly Redeemed';
  return 'Status: Redeemed';
}

export type GiftSuccessCaller = 'invite' | 'transfer' | 'share';

export function transferSuccessWho(member: {
  channelName?: string | null;
  displayName?: string | null;
} | null | undefined): string {
  const channel = String(member?.channelName || '').trim();
  if (channel) return `@${channel}`;
  const displayName = String(member?.displayName || '').trim();
  if (displayName) return displayName;
  return 'this member';
}

export function giftSuccessDialog(input: {
  caller: GiftSuccessCaller;
  count: number;
  scope: 'SINGLE' | 'DUAL';
  who: string;
}): { title: string; body: string } {
  const title = input.caller === 'transfer'
    ? 'LA Pass Gift Card Sent!'
    : 'LA Pass Gift Card Link Created!';
  const count = Number.isInteger(input.count) && input.count > 0 ? input.count : 1;
  const kind = input.scope === 'DUAL' ? 'Dual' : 'Single';
  const noun = count === 1 ? 'Gift Card' : 'Gift Cards';
  return {
    title,
    body: `Reserved ${count} ${kind} LA Pass ${noun} for ${input.who}.`,
  };
}

export const EXPIRY_LABEL: Record<ExpireChoice, string> = {
  1: '1 day',
  3: '3 days',
  7: '7 days',
  14: '14 days',
  21: '21 days',
  custom: 'Custom (1–90 days)',
  never: 'Never',
};

export function expiryChoiceLabel(choice: ExpireChoice, daysLeft: number | null): string {
  if (choice !== 'custom') return EXPIRY_LABEL[choice];
  const max = customDayMax(daysLeft);
  if (max >= 1 && max < 90) return `Custom (1–${max} days)`;
  return EXPIRY_LABEL.custom;
}
